import { getBuildByPatchInfo } from 'web/data/core-data'
import { BuildData, FightEvent, FightPlayer, FightSetup, FullFight, Spec, Spell, SpellAssignment } from 'web/data/models'
import { Interval, interval, isInsideAnyInterval, isInsideInterval } from 'web/utils/interval-utils'
import { sortFightEvents } from 'web/utils/sort-utils'
import { assertDefined, expectOneById, GroupedList, groupList, splitList } from 'web/utils/utils'
import { resolveSpellCooldown } from './cooldowns/core-resolver'

/**
 * Calculates what spells the given player has access to based on
 * their talent/modifier configuration.
 */
export function getPlayerSpells(fightPlayer: FightPlayer): Spell[] {

    const buildData = getBuildByPatchInfo(fightPlayer.patchInfo)
    const spec = buildData.Spec(fightPlayer.specId)

    const spells = spec.spellIds.reduce<Spell[]>((acc, curr) => {
        const spell = buildData.Spell(curr)

        const requiredTalentMissing = spell.talentId != null
            && fightPlayer.talentIds.includes(spell.talentId) === false

        if (requiredTalentMissing) {
            return acc
        }

        const requiredModifierMissing = spell.modifierId != null
            && fightPlayer.modifierIds.includes(spell.modifierId) === false

        if (requiredModifierMissing) {
            return acc
        }

        // A talent is overriding this spell, skip
        if (spell.replacedByTalentId != null
            && fightPlayer.talentIds.includes(spell.replacedByTalentId)) {

            return acc
        }

        return [
            ...acc,
            spell
        ]

    }, [])

    return spells
}

export function getPlayerSpec(fightPlayer: FightPlayer): Spec {
    return getBuildByPatchInfo(fightPlayer.patchInfo)
        .Spec(fightPlayer.specId)
}

/**
 * Used to create a unique key for any given player spell tied to a
 * specific event
 */
export function uniqueIdFromSpellAssignment(sa: { fightEventId: string; playerId: string; spellId: string }) {
    return `${sa.fightEventId}_${sa.playerId}_${sa.spellId}`
}

/**
 * Given a cooldown, target assignment and list of events, returns all
 * events that are inside the deadzone.
 */
export function getAllEventsInDeadzone({
    spellCooldown,
    assignedEventId,
    events
}: {
    spellCooldown: number
    assignedEventId: string
    events: FightEvent[]
}) {

    const { taken, remaining: remainingEvents } = splitList(
        events,
        (item) => item.id === assignedEventId
    )

    const assignedEvent = taken[0]
    assertDefined(assignedEvent)

    const deadzone = {
        from: assignedEvent.offset - spellCooldown,
        to: assignedEvent.offset + spellCooldown
    }

    return remainingEvents.filter(event => isInsideInterval({
        interval: deadzone,
        value: event.offset,

        // Turn inclusive off, it's OK for a CD to come back the same
        // second an event happens. If you turn this on, a 3min CD
        // will not be available at 03:00 if used at 00:00.
        inclusive: false
    }))
}

type ConstructorInput = {
    fullFight: FullFight
    setupState: FightSetup
}

/**
 * Encapsulates common operations for working with spells in a
 * fight-context
 */
export class SpellEngine {

    private setupState: FightSetup
    private buildData: BuildData

    private sortedEvents: FightEvent[]
    private assignmentsByPlayerId: GroupedList<SpellAssignment>

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Private Constructor
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    constructor(states: ConstructorInput) {
        const { fullFight, setupState } = states

        this.buildData = getBuildByPatchInfo(setupState.patchInfo)
        this.setupState = setupState

        this.assignmentsByPlayerId = groupList(
            setupState.spellAssignments,
            (item) => item.playerId
        )

        this.sortedEvents = [...fullFight.events]
            .sort(sortFightEvents)
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Static Public API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    public static fromStates(states: ConstructorInput) {
        return new SpellEngine(
            JSON.parse(
                JSON.stringify(states)
            )
        )
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Public API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    public getInternalState() {
        return {
            sortedEvents: this.sortedEvents,
        }
    }

    /**
     * For the given player/spell combo, returns all events where that
     * combination can fit according to the cooldown for that spell.
     */
    public getViableEventsForSpell(params: { playerId: string; spellId: string }): FightEvent[] {
        const spell = this.buildData.Spell(params.spellId)
        const player = expectOneById(this.setupState.fightPlayers, params.playerId)

        const playerAssignments = this.assignmentsByPlayerId.get(params.playerId)

        // No assignments, all events are fair game
        if (playerAssignments.length <= 0) {
            return this.sortedEvents
        }

        const eventsWithAssignments = this.sortedEvents.filter(event => {
            return playerAssignments.some(pa => {
                return pa.spellId === spell.id && pa.fightEventId === event.id
            })
        })

        // There's as many assignments of this spell as there are
        // events, safe to assume we won't fit any more
        if (this.sortedEvents.length <= eventsWithAssignments.length) {
            return []
        }

        const spellCdDuration = resolveSpellCooldown(spell.id, player)

        // Iterate all events where we have assignments and calculate
        // the deadzone around those offsets where another assignment
        // with this cooldown duration won't fit
        const deadzones = eventsWithAssignments.map<Interval>(event => {
            return {
                from: event.offset - spellCdDuration,
                to: event.offset + spellCdDuration
            }
        })

        const eventsWithoutAssignments = this.sortedEvents
            .filter(event => eventsWithAssignments.includes(event) === false)

        // Grab all events that can fit around the deadzones we
        // calculated
        const validEvents = eventsWithoutAssignments.filter(event => {

            return isInsideAnyInterval({
                intervals: deadzones,
                value: event.offset,
                inclusive: false
            }) === false
        })

        return validEvents
    }

    /**
     * For the given assignment, return events that are outside all
     * deadzones of that spell.
     */
    public getEventsOutsideAllDeadzones(spellAssignment: SpellAssignment) {
        const player = expectOneById(this.setupState.fightPlayers, spellAssignment.playerId)
        const cooldown = resolveSpellCooldown(spellAssignment.spellId, player)

        const deadzones = this.setupState.spellAssignments
            .filter(sa => {
                return (
                    sa.playerId === spellAssignment.playerId &&
                    sa.spellId === spellAssignment.spellId
                )
            })
            .map(sa => {
                const event = expectOneById(this.sortedEvents, sa.fightEventId)
                return interval(
                    event.offset - cooldown,
                    event.offset + cooldown
                )
            })

        const outsideEvents = this.sortedEvents.filter(event => {
            return deadzones.some(deadzone => isInsideInterval({
                interval: deadzone,
                value: event.offset,
                inclusive: false
            })) === false
        })

        return outsideEvents
    }

    /**
     * For the given assignment, checks if there are other assignments
     * in the fight that are inside the cooldown-deadzone of this
     * assignment.
     *
     * If there is, returns true, otherwise false.
     */
    public isAssignmentOverlapping(spellAssignment: SpellAssignment) {
        const player = expectOneById(this.setupState.fightPlayers, spellAssignment.playerId)
        const cooldown = resolveSpellCooldown(spellAssignment.spellId, player)

        const deadzoneEventIds = getAllEventsInDeadzone({
            events: this.sortedEvents,
            spellCooldown: cooldown,
            assignedEventId: spellAssignment.fightEventId
        }).map(ev => ev.id)

        const assignedEventIds = this.assignmentsByPlayerId.get(player.id)
            .filter(sa => uniqueIdFromSpellAssignment(sa) !== uniqueIdFromSpellAssignment(spellAssignment))
            .filter(sa => sa.spellId === spellAssignment.spellId)
            .map(sa => sa.fightEventId)

        return deadzoneEventIds.some(evId => assignedEventIds.includes(evId))
    }

    /**
     * Returns information about the past and future of the given
     * spell assignment.
     */
    public getSurroundingInfo(spellAssignment: SpellAssignment): {
        eventOffset: number
        prevUseOffset: number | undefined
        nextUseOffset: number | undefined
        prevHeldFor: number | undefined
        nextHeldFor: number | undefined
        prevExtraUseLatest: Interval | undefined
        nextExtraUseLatest: Interval | undefined
    } {

        const allSpellAssignments = this.setupState.spellAssignments
            .filter(sa => {
                return (
                    sa.playerId === spellAssignment.playerId &&
                    sa.spellId === spellAssignment.spellId
                )
            })

        const player = expectOneById(this.setupState.fightPlayers, spellAssignment.playerId)
        const cooldown = resolveSpellCooldown(spellAssignment.spellId, player)

        const sortedMetaData = this.sortedEvents.flatMap(event => {
            const assignment = allSpellAssignments.find(sa => sa.fightEventId === event.id)

            if (assignment) {
                return [{
                    event,
                    assignment,

                    /**
                     * Interval when adding event offset + cooldown
                     */
                    singleUseInterval: interval(
                        event.offset,
                        event.offset + cooldown
                    ),

                    /**
                     * This interval accounts for two uses, starting
                     * at the event offset
                     */
                    doubleUseInterval: interval(
                        event.offset,
                        event.offset + cooldown * 2
                    )
                }]
            }

            return []
        })

        const currentIndex = sortedMetaData.findIndex(
            data => data.assignment.fightEventId === spellAssignment.fightEventId
        )

        const current = sortedMetaData[currentIndex]
        let previous = undefined
        let next = undefined

        if (currentIndex > 0) {
            previous = sortedMetaData[currentIndex - 1]
        }

        if (currentIndex < sortedMetaData.length) {
            next = sortedMetaData[currentIndex + 1]
        }

        const returnData: {
            eventOffset: number
            prevUseOffset: number | undefined
            nextUseOffset: number | undefined
            prevHeldFor: number | undefined
            nextHeldFor: number | undefined
            prevExtraUseLatest: Interval | undefined
            nextExtraUseLatest: Interval | undefined
        } = {
            eventOffset: current.event.offset,
            prevUseOffset: (previous && previous.event.offset),
            nextUseOffset: (next && next.event.offset),
            prevHeldFor: undefined,
            nextHeldFor: undefined,
            prevExtraUseLatest: undefined,
            nextExtraUseLatest: undefined
        }

        /*
            Calculate extra uses
        */

        // One more use available backwards
        if (previous && (current.singleUseInterval.from - cooldown) >= previous.singleUseInterval.to) {
            returnData.prevExtraUseLatest = {
                from: previous.singleUseInterval.to,
                to: current.singleUseInterval.from - cooldown
            }
        }

        // If previous was null, also check if we can use it on pull
        if (previous == null) {
            if (current.singleUseInterval.from - cooldown >= 0) {
                returnData.prevExtraUseLatest = {
                    from: 0,
                    to: current.singleUseInterval.from - cooldown
                }
            }
        }

        // One more use forward
        if (next && (current.doubleUseInterval.to) <= next.singleUseInterval.from) {
            returnData.nextExtraUseLatest = {
                from: current.singleUseInterval.to,
                to: next.singleUseInterval.from - cooldown
            }
        }

        /*
            Calculate held-for durations

            These can resolve to negative values when assignments
            overlap, in those cases we simply say they weren't held at
            all.
        */

        if (returnData.prevUseOffset != null) {
            const heldFor = returnData.eventOffset - (returnData.prevUseOffset + cooldown)
            returnData.prevHeldFor = (heldFor > 0) ? heldFor : undefined
        }

        if (returnData.nextUseOffset != null) {
            const heldFor = returnData.nextUseOffset - (returnData.eventOffset + cooldown)
            returnData.nextHeldFor = (heldFor > 0) ? heldFor : undefined
        }

        return returnData
    }

}

